/**
 * Cart-integrity guard for the Express Delivery add-on (loaded globally,
 * see layout/theme.liquid, so it runs on the cart page and everywhere else -
 * not just product pages).
 *
 * Express Delivery is added as its own separate cart line (see
 * hh-express-delivery.js), tagged with a hidden `_hh_linked_variant`
 * property naming the watch variant it was added alongside. Shopify's cart
 * has no native concept of one line being "attached to" another, so if the
 * customer removes that watch from the cart - or changes its size/case-back,
 * which is a different variant id - the Express line has no reason to go
 * away on its own. Left alone, that lets a customer reach checkout with a
 * same-day-shipping charge attached to a watch that isn't actually in the
 * cart, or to nothing at all. This watches for cart changes and removes any
 * Express Delivery line whose linked variant is no longer present.
 *
 * Only lines that HAVE the link property are touched - an Express line added
 * before this fix shipped (no property) is left alone rather than guessed
 * at, since there's nothing reliable to check it against.
 *
 * FIXED 2026-09-26: the /cart/change.js call here didn't request `sections`,
 * so assets/component-cart-items.js's cart:update handler had no HTML to
 * morph with directly and fell back to sectionRenderer.renderSection() - an
 * entirely separate, independent fetch + morph cycle stacking on top of the
 * one the native removal had just triggered a moment earlier. That extra,
 * redundant morph immediately after another one is exactly where the cart
 * drawer's open dialog was losing its open state and not recovering (even
 * with hh-cart-drawer-fix.js's widened attribute watch) - confirmed by
 * reproducing it live: removing a watch that had Express attached (which
 * triggers this guard) closed the drawer even though other items remained in
 * the cart, while a plain removal that never touches this guard did not.
 * Now requesting `sections` here too, exactly like hh-express-delivery.js
 * and snippets/product-upgrades.liquid already do, so cart-items-component
 * can take its fast, direct morph path instead - one morph instead of two.
 */
(function () {
  var EXPRESS_HANDLE = 'express-delivery';
  var LINK_PROPERTY = '_hh_linked_variant';
  var SELF_SOURCE = 'hh-express-delivery-guard';
  var checking = false;

  function getCartSectionIds() {
    return Array.prototype.map
      .call(document.querySelectorAll('cart-items-component'), function (el) {
        return el.dataset.sectionId;
      })
      .filter(Boolean);
  }

  function removeSequentially(items, sectionIds) {
    return items.reduce(function (chain, item) {
      return chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0, sections: sectionIds.join(',') }),
        }).then(function (r) {
          return r.json();
        });
      });
    }, Promise.resolve(null));
  }

  function checkCart() {
    if (checking) return;
    checking = true;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var presentVariantIds = cart.items.map(function (item) {
          return String(item.variant_id);
        });

        var toRemove = cart.items.filter(function (item) {
          if (item.handle !== EXPRESS_HANDLE) return false;
          var linkedId = item.properties && item.properties[LINK_PROPERTY];
          if (!linkedId) return false;
          return presentVariantIds.indexOf(String(linkedId)) === -1;
        });

        if (!toRemove.length) return null;

        var sectionIds = getCartSectionIds();

        return removeSequentially(toRemove, sectionIds).then(function (lastResponse) {
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: {},
                sourceId: SELF_SOURCE,
                data: {
                  source: SELF_SOURCE,
                  sections: lastResponse && lastResponse.sections,
                },
              },
            })
          );
        });
      })
      .catch(function (err) {
        console.error('[hh-express-delivery-guard]', err);
      })
      .finally(function () {
        checking = false;
      });
  }

  document.addEventListener('cart:update', function (e) {
    if (e.detail && e.detail.data && e.detail.data.source === SELF_SOURCE) return;
    checkCart();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkCart);
  } else {
    checkCart();
  }
})();
